import { useRef, useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  MapPin,
  AlertTriangle,
  Star,
  ChevronLeft,
  ImagePlus,
  Loader2,
  X,
  Video,
  Navigation,
} from 'lucide-react';
import { StatusBadge, Badge } from '../../shared/ui/Badge';
import { Button } from '../../shared/ui/Button';
import { Spinner } from '../../shared/ui/Spinner';
import { Skeleton } from '../../shared/ui/Skeleton';
import { Modal } from '../../shared/ui/Modal';
import { ConfirmModal } from '../../shared/ui/ConfirmModal';
import { ErrorState } from '../../shared/ui/ErrorState';
import { AnimateIn } from '../../shared/ui/AnimateIn';
import { useToast } from '../../shared/ui/Toast';
import { usePageMeta } from '../../shared/hooks/usePageMeta';
import { useRequest, useCancelRequest, useDeleteRequest } from '../../features/requests/hooks';
import { useAcceptQuote, useSubmitCounterOffer } from '../../features/quotes/hooks';
import { useCreateDispute, useDisputeByRequest } from '../../features/disputes/hooks';
import { useReviewByRequest, useCreateReview } from '../../features/reviews/hooks';
import { useInvoiceByRequest } from '../../features/invoices/hooks';
import { useBankInfo } from '../../features/settings/hooks';
import { joinRequest, leaveRequest, onRequestStatus } from '../../services/socket';
import { uploadDisputeEvidence } from '../../shared/api/cloudinary';
import { fmt } from '../../shared/constants/pricing';
import type { RequestStatus } from '../../shared/types/app';

const MAX_EVIDENCE_PHOTOS = 3;
const ALLOWED_EVIDENCE_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp']);
const MAX_EVIDENCE_BYTES = 10 * 1024 * 1024; // 10 MB, matches the backend's presigned-upload limit

// Must match the backend's actual rule (rtBackend/src/features/disputes/service.ts's
// DISPUTABLE_STATUSES) — a dispute can only be filed once the job is actively moving or done,
// not while it's still Assigned/EnRoute-to-pickup/Loading.
const DISPUTABLE_STATUSES: RequestStatus[] = ['InTransit', 'Delivered'];

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col">
      <p className="text-[11px] font-bold text-muted uppercase tracking-widest mb-1">{label}</p>
      <p className="text-sm text-text">{value}</p>
    </div>
  );
}

function formatQuoteExpiry(validUntil: string) {
  const diff = new Date(validUntil).getTime() - Date.now();
  if (diff <= 0) return 'Expired';
  const minutes = Math.ceil(diff / 60000);
  if (minutes < 60) return `Expires in ${minutes} minute${minutes === 1 ? '' : 's'}`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `Expires in ${hours} hour${hours === 1 ? '' : 's'}`;
  const days = Math.floor(hours / 24);
  return `Expires in ${days} day${days === 1 ? '' : 's'}`;
}

export function RequestDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [cancelOpen, setCancelOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [acceptOpen, setAcceptOpen] = useState(false);
  const [disputeOpen, setDisputeOpen] = useState(false);
  const [disputeText, setDisputeText] = useState('');
  const [disputeEvidence, setDisputeEvidence] = useState<string[]>([]);
  const [uploadingEvidence, setUploadingEvidence] = useState(false);
  const [evidenceError, setEvidenceError] = useState<string | null>(null);
  const evidenceInputRef = useRef<HTMLInputElement>(null);
  const [counterOpen, setCounterOpen] = useState(false);
  const [counterAmount, setCounterAmount] = useState('');
  const [counterNote, setCounterNote] = useState('');
  const [reviewRating, setReviewRating] = useState(0);
  const [reviewComment, setReviewComment] = useState('');
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);

  const { data: request, isLoading, isError, refetch } = useRequest(id!);
  usePageMeta(request ? `${request.equipmentKind} Request` : 'Request Details');
  const cancelRequest = useCancelRequest();
  const deleteRequest = useDeleteRequest();
  const acceptQuote = useAcceptQuote();
  const submitCounterOffer = useSubmitCounterOffer();
  const createDispute = useCreateDispute();
  const { data: dispute } = useDisputeByRequest(id!, true);
  const { data: existingReview } = useReviewByRequest(
    id!,
    request?.status === 'Delivered',
  );
  const createReview = useCreateReview();
  // Invoice now exists from the moment a quote is accepted (see rtBackend's quotes/service.ts),
  // not just once delivered — a 404 before that point is expected and harmless (retry: false).
  const invoiceEnabled = request != null && request.status !== 'Pending' && request.status !== 'Quoted';
  const { data: invoice } = useInvoiceByRequest(id!, invoiceEnabled);
  // Only fetched once the modal actually opens — no reason to hit the bank-details endpoint on
  // every delivered-request page view before the customer has asked to see it.
  const { data: bankInfo, isLoading: bankInfoLoading } = useBankInfo(paymentModalOpen);

  // Join socket room and listen for real-time status updates
  useEffect(() => {
    if (!id) return;
    joinRequest(id);
    const unsub = onRequestStatus(({ requestId: rid }) => {
      if (rid === id) queryClient.invalidateQueries({ queryKey: ['requests', id] });
    });
    return () => {
      leaveRequest(id);
      unsub();
    };
  }, [id, queryClient]);

  if (isLoading) {
    return (
      <div className="flex flex-col max-w-2xl mx-auto w-full gap-3">
        <div className="bg-surface rounded-2xl p-4 flex items-center justify-between gap-3">
          <div className="flex-1 flex flex-col gap-2">
            <Skeleton className="h-3 w-16" />
            <Skeleton className="h-6 w-40" />
            <Skeleton className="h-3 w-28" />
          </div>
          <Skeleton className="h-6 w-20 rounded-full" />
        </div>
        <div className="bg-surface rounded-2xl p-4 flex flex-col gap-3">
          <Skeleton className="h-3 w-16" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-2/3" />
        </div>
        <div className="bg-surface rounded-2xl p-4 flex flex-col gap-3">
          <Skeleton className="h-3 w-16" />
          <div className="grid grid-cols-2 gap-4">
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
          </div>
        </div>
      </div>
    );
  }

  if (isError || !request) {
    return (
      <div className="flex flex-col max-w-2xl mx-auto w-full">
        <ErrorState message="Could not load request details." onRetry={() => refetch()} />
      </div>
    );
  }

  // Backend is the authoritative check (quote.validUntil) — this is purely to avoid showing a
  // doomed "Accept Quote" button; if a race lets it through anyway, the mutation's error toast
  // and the requests-query invalidation below still handle it correctly.
  const quoteExpiresAt = request.quote ? new Date(request.quote.validUntil) : null;
  const isQuoteExpired = quoteExpiresAt ? quoteExpiresAt.getTime() < Date.now() : false;
  const quoteExpiryLabel = request.quote ? formatQuoteExpiry(request.quote.validUntil) : '';
  const quoteExpiryAt = quoteExpiresAt ? quoteExpiresAt.toLocaleString() : '';

  // Same idea: backend enforces the 2-day-post-delivery dispute window, this just avoids
  // showing a doomed "Raise a Dispute" button once it's passed.
  const isDisputeWindowExpired = request.status === 'Delivered' && !!request.updatedAt
    && Date.now() - new Date(request.updatedAt).getTime() > 2 * 24 * 60 * 60 * 1000;

  const handleCancel = async () => {
    try {
      await cancelRequest.mutateAsync(request.id);
      toast('Request cancelled.', 'info');
      navigate(-1);
    } catch (err) {
      toast((err as Error).message || 'Failed to cancel', 'error');
    }
  };

  // Unlike Cancel, the record is actually gone afterward — navigate to a known destination
  // rather than relying on browser history (navigate(-1) could land back on this now-404 page).
  const handleDelete = async () => {
    try {
      await deleteRequest.mutateAsync(request.id);
      toast('Request deleted.', 'info');
      navigate('/app/requests');
    } catch (err) {
      toast((err as Error).message || 'Failed to delete', 'error');
    }
  };

  const handleAcceptQuote = async () => {
    try {
      await acceptQuote.mutateAsync(request.id);
      toast('Quote accepted, your job is confirmed!', 'success');
      setAcceptOpen(false);
    } catch (err) {
      toast((err as Error).message || 'Failed to accept quote', 'error');
    }
  };

  const handleReviewSubmit = async () => {
    if (!reviewRating || !request.assignedDriverId) return;
    try {
      await createReview.mutateAsync({
        requestId: request.id,
        revieweeId: request.assignedDriverId,
        rating: reviewRating,
        comment: reviewComment.trim() || undefined,
      });
      toast('Review submitted. Thank you!', 'success');
      setReviewRating(0);
      setReviewComment('');
    } catch (err) {
      toast((err as Error).message || 'Failed to submit review', 'error');
    }
  };

  const handleCounterSubmit = async () => {
    const amount = Number(counterAmount);
    if (!amount || amount <= 0) return;
    try {
      await submitCounterOffer.mutateAsync({ requestId: request.id, amount, note: counterNote.trim() || undefined });
      toast('Counter-offer sent, we\'ll respond shortly.', 'success');
      setCounterOpen(false);
      setCounterAmount('');
      setCounterNote('');
    } catch (err) {
      toast((err as Error).message || 'Failed to send counter-offer', 'error');
    }
  };

  const handleEvidenceFile = async (file: File | undefined) => {
    if (!file) return;
    setEvidenceError(null);

    if (!ALLOWED_EVIDENCE_TYPES.has(file.type)) {
      setEvidenceError('Please choose a JPEG, PNG, or WebP image.');
      return;
    }
    if (file.size > MAX_EVIDENCE_BYTES) {
      setEvidenceError('That file is too large, please choose one under 10 MB.');
      return;
    }

    setUploadingEvidence(true);
    try {
      const url = await uploadDisputeEvidence(file);
      setDisputeEvidence((prev) => [...prev, url]);
    } catch {
      setEvidenceError("Couldn't upload that photo, please try again.");
    } finally {
      setUploadingEvidence(false);
      if (evidenceInputRef.current) evidenceInputRef.current.value = '';
    }
  };

  const handleDisputeSubmit = async () => {
    if (!disputeText.trim()) return;
    try {
      await createDispute.mutateAsync({ requestId: request.id, description: disputeText.trim(), evidence: disputeEvidence });
      toast('Dispute filed. Our team will review it shortly.', 'success');
      setDisputeOpen(false);
      setDisputeText('');
      setDisputeEvidence([]);
    } catch (err) {
      toast((err as Error).message || 'Failed to file dispute', 'error');
    }
  };

  return (
    <div className="flex flex-col max-w-2xl mx-auto w-full gap-3">

      {/* Back */}
      <Link
        to="/app/requests"
        className="inline-flex items-center gap-1.5 text-xs text-muted hover:text-text transition-colors mb-1"
      >
        <ChevronLeft size={13} />
        My Requests
      </Link>

      {/* Header card */}
      <AnimateIn animation="fade-up">
      <div className="bg-surface rounded-2xl p-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-[11px] font-bold text-muted uppercase tracking-widest mb-1">Request</p>
            <h1 className="text-2xl font-black text-text">{request.equipmentKind}</h1>
            <p className="text-xs text-muted mt-1">
              {[request.make, request.model].filter(Boolean).join(' ') || 'Details'} ·{' '}
              <span className="font-mono">#{request.id.slice(-6).toUpperCase()}</span>
            </p>
          </div>
          <StatusBadge status={request.status} />
        </div>
      </div>
      </AnimateIn>

      {/* Price card — appears once admin sets the quote; joins the same socket room as status
          updates above, so it appears automatically without a manual refresh. */}
      {request.quote && (
        <AnimateIn animation="fade-up" delay={60}>
        <div className="bg-surface rounded-2xl p-4">
          <p className="text-[11px] font-bold text-muted uppercase tracking-widest mb-3">Price</p>
          <div className="flex flex-col gap-2">
            {request.quote.handlingFee > 0 && (
              <div className="flex items-center justify-between text-sm">
                <span className="text-text-secondary">Handling fee</span>
                <span className="text-text">{fmt(request.quote.handlingFee)}</span>
              </div>
            )}
            {request.quote.transportFee > 0 && (
              <div className="flex items-center justify-between text-sm">
                <span className="text-text-secondary">Transport fee</span>
                <span className="text-text">{fmt(request.quote.transportFee)}</span>
              </div>
            )}
            {request.quote.permitFee > 0 && (
              <div className="flex items-center justify-between text-sm">
                <span className="text-text-secondary">Permit fee</span>
                <span className="text-text">{fmt(request.quote.permitFee)}</span>
              </div>
            )}
            {request.quote.surcharge > 0 && (
              <div className="flex items-center justify-between text-sm">
                <span className="text-text-secondary">Same-day surcharge</span>
                <span className="text-text">{fmt(request.quote.surcharge)}</span>
              </div>
            )}
            <div className="h-px bg-line my-1" />
            <div className="flex items-center justify-between">
              <span className="text-sm font-bold text-text">Total</span>
              <span className="text-lg font-black text-accent">{fmt(request.quote.total)}</span>
            </div>
          </div>
          {isQuoteExpired && (
            <p className="text-xs text-warning mt-3">
              This quote has expired. Our team will follow up with an updated price.
            </p>
          )}
          {request.quote.counterOfferStatus === 'PENDING' && (
            <p className="text-xs text-muted mt-3">
              Your counter-offer of {fmt(request.quote.counterOfferAmount ?? 0)} is awaiting a response.
            </p>
          )}
          {request.quote.counterOfferStatus === 'DECLINED' && (
            <p className="text-xs text-warning mt-3">
              Your counter-offer was declined. The original quote above still stands.
            </p>
          )}
        </div>
        </AnimateIn>
      )}

      {/* Payment card — sits right under Price, since settling the bill matters more than most
          of what's below it. An invoice exists from the moment a quote is accepted (not just on
          delivery — see rtBackend's quotes/service.ts), and the customer can pay any time from
          then on — some want to settle up front, some prefer to wait until the job's done, both
          are fine, so the button is available the whole way through rather than gated to Delivered. */}
      {request.quote && (
        <AnimateIn animation="fade-up" delay={75}>
          <div className={`rounded-2xl border p-4 ${isQuoteExpired ? 'border-danger/20 bg-danger/10' : 'border-accent/20 bg-accent/10'}`}>
            <p className={`text-[11px] font-bold uppercase tracking-widest ${isQuoteExpired ? 'text-danger' : 'text-accent'}`}>
              {isQuoteExpired ? 'Quote expired' : 'Quote expiry'}
            </p>
            <p className="text-xs text-text-secondary mt-1">
              {isQuoteExpired
                ? `This quote expired on ${quoteExpiryAt}. Please wait for an updated price or ask the team to extend it.`
                : `This quote expires ${quoteExpiryLabel.toLowerCase()} at ${quoteExpiryAt}.`}
            </p>
          </div>
        </AnimateIn>
      )}

      {invoice && (
        invoice.status === 'paid' ? (
          <AnimateIn animation="fade-up" delay={90}>
          <div className="bg-surface rounded-2xl p-4 flex items-center justify-between">
            <p className="text-[11px] font-bold text-muted uppercase tracking-widest">Payment</p>
            <Badge label="Paid" variant="success" />
          </div>
          </AnimateIn>
        ) : (
          <AnimateIn animation="fade-up" delay={90}>
          <div className="bg-surface rounded-2xl p-4">
            <div className="flex items-center justify-between mb-3">
              <p className="text-[11px] font-bold text-muted uppercase tracking-widest">Payment</p>
              <span className="text-lg font-black text-accent">{fmt(invoice.total)}</span>
            </div>
            <Button onClick={() => setPaymentModalOpen(true)} className="!text-sm normal-case !tracking-normal rounded-2xl !px-4 !py-3">
              View Payment Info
            </Button>
          </div>
          </AnimateIn>
        )
      )}

      {/* Route card */}
      <AnimateIn animation="fade-up" delay={120}>
      <div className="bg-surface rounded-2xl p-4">
        <p className="text-[11px] font-bold text-muted uppercase tracking-widest mb-3">Route</p>
        <div className="flex flex-col gap-0">
          <div className="flex items-center gap-3">
            <MapPin size={14} className="text-accent flex-shrink-0" />
            <span className="text-sm text-text">{request.pickup}</span>
          </div>
          <div className="ml-[6px] pl-[1px] border-l border-dashed border-line h-4" />
          <div className="flex items-center gap-3">
            <MapPin size={14} className="text-muted flex-shrink-0" />
            <span className="text-sm text-text-secondary">{request.dropoff}</span>
          </div>
        </div>
        {request.distanceKm > 0 && (
          <p className="text-xs text-muted mt-3">{request.distanceKm} km</p>
        )}
        {request.pickupLat != null && request.pickupLng != null && request.dropoffLat != null && request.dropoffLng != null && (
          <a
            href={`https://www.google.com/maps/dir/?api=1&origin=${request.pickupLat},${request.pickupLng}&destination=${request.dropoffLat},${request.dropoffLng}`}
            target="_blank"
            rel="noreferrer"
            className="mt-3 inline-flex items-center gap-1.5 text-xs font-bold text-accent hover:underline"
          >
            <Navigation size={12} />
            Open in Maps
          </a>
        )}
      </div>
      </AnimateIn>

      {/* Pickup / drop-off media — captured by the driver as proof of service */}
      {((request.pickupMedia?.length ?? 0) > 0 || (request.deliveryMedia?.length ?? 0) > 0) && (
        <AnimateIn animation="fade-up" delay={180}>
        <div className="bg-surface rounded-2xl p-4">
          <p className="text-[11px] font-bold text-muted uppercase tracking-widest mb-3">Pickup &amp; Delivery Media</p>
          {(request.pickupMedia?.length ?? 0) > 0 && (
            <div className="mb-3">
              <p className="text-xs text-muted mb-2">Pickup</p>
              <div className="flex gap-2 overflow-x-auto">
                {request.pickupMedia!.map((m, i) => (
                  <a key={`${m.url}-${i}`} href={m.url} target="_blank" rel="noreferrer" className="flex-shrink-0 h-20 w-20 rounded-lg bg-bg overflow-hidden flex items-center justify-center">
                    {m.type === 'video' ? <Video size={20} className="text-muted" /> : <img src={m.url} alt="Pickup" className="h-full w-full object-cover" />}
                  </a>
                ))}
              </div>
            </div>
          )}
          {(request.deliveryMedia?.length ?? 0) > 0 && (
            <div>
              <p className="text-xs text-muted mb-2">Delivery</p>
              <div className="flex gap-2 overflow-x-auto">
                {request.deliveryMedia!.map((m, i) => (
                  <a key={`${m.url}-${i}`} href={m.url} target="_blank" rel="noreferrer" className="flex-shrink-0 h-20 w-20 rounded-lg bg-bg overflow-hidden flex items-center justify-center">
                    {m.type === 'video' ? <Video size={20} className="text-muted" /> : <img src={m.url} alt="Delivery" className="h-full w-full object-cover" />}
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
        </AnimateIn>
      )}

      {/* Details card */}
      <AnimateIn animation="fade-up" delay={240}>
      <div className="bg-surface rounded-2xl p-4">
        <p className="text-[11px] font-bold text-muted uppercase tracking-widest mb-3">Details</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-4">
          <Field label="Pickup date" value={request.dateLabel} />
          {request.timeSlot && <Field label="Pickup time" value={request.timeSlot} />}
          <Field label="Weight class" value={request.weightClass.replace('_', ' ')} />
          {request.estimatedWeight != null && (
            <Field label="Est. weight" value={`${request.estimatedWeight.toLocaleString()} tons`} />
          )}
        </div>
      </div>
      </AnimateIn>

      {/* Driver card */}
      {request.assignedDriverName && (
        <AnimateIn animation="fade-up" delay={300}>
        <div className="bg-surface rounded-2xl p-4">
          <p className="text-[11px] font-bold text-muted uppercase tracking-widest mb-3">Driver</p>
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-accent flex items-center justify-center text-black text-sm font-black flex-shrink-0">
              {request.assignedDriverName.charAt(0)}
            </div>
            <div>
              <p className="text-sm font-bold text-text">{request.assignedDriverName}</p>
              {request.assignedDriverPhone && (
                <a href={`tel:${request.assignedDriverPhone}`} className="text-xs text-accent hover:underline">
                  {request.assignedDriverPhone}
                </a>
              )}
            </div>
          </div>
        </div>
        </AnimateIn>
      )}

      {/* Review card */}
      {request.status === 'Delivered' && (
        existingReview ? (
          <AnimateIn animation="fade-up" delay={360}>
          <div className="bg-surface rounded-2xl p-4">
            <div className="flex items-center justify-between mb-3">
              <p className="text-[11px] font-bold text-muted uppercase tracking-widest">Your Review</p>
              {existingReview.status === 'Pending' && <Badge label="Pending approval" variant="warning" />}
              {existingReview.status === 'Approved' && <Badge label="Approved" variant="success" />}
              {existingReview.status === 'Rejected' && <Badge label="Not approved" variant="danger" />}
            </div>
            {existingReview.status === 'Rejected' ? (
              <p className="text-sm text-muted">This review wasn't approved for display.</p>
            ) : (
              <>
                <div className="flex gap-1 mb-2">
                  {[1, 2, 3, 4, 5].map((s) => (
                    <Star key={s} size={16} className={s <= existingReview.rating ? 'text-accent fill-accent' : 'text-muted'} />
                  ))}
                </div>
                {existingReview.comment && (
                  <p className="text-sm text-text-secondary mt-1">{existingReview.comment}</p>
                )}
              </>
            )}
          </div>
          </AnimateIn>
        ) : request.assignedDriverId ? (
          <AnimateIn animation="fade-up" delay={360}>
          <div className="bg-surface rounded-2xl p-4">
            <p className="text-[11px] font-bold text-muted uppercase tracking-widest mb-3">Rate your experience</p>
            <div className="flex gap-2 mb-3">
              {[1, 2, 3, 4, 5].map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setReviewRating(s)}
                  aria-label={`Rate ${s} star${s > 1 ? 's' : ''}`}
                  aria-pressed={s <= reviewRating}
                  className="transition-transform hover:scale-110"
                >
                  <Star size={24} className={s <= reviewRating ? 'text-accent fill-accent' : 'text-muted'} />
                </button>
              ))}
            </div>
            <textarea
              rows={3}
              value={reviewComment}
              onChange={(e) => setReviewComment(e.target.value)}
              placeholder="Leave a comment (optional)..."
              className="w-full rounded-xl border border-line bg-surface-raised px-3 py-2.5 text-sm text-text placeholder-muted outline-none focus:border-accent transition-colors resize-none mb-3"
            />
            {reviewRating === 0 && (
              <p className="text-xs text-muted mb-3">Please select a rating to submit a review.</p>
            )}
            <Button onClick={handleReviewSubmit} loading={createReview.isPending} disabled={!reviewRating}>
              Submit Review
            </Button>
          </div>
          </AnimateIn>
        ) : null
      )}

      {/* Your Dispute card */}
      {dispute && (
        <AnimateIn animation="fade-up" delay={480}>
        <div className="bg-surface rounded-2xl p-4">
          <div className="flex items-center justify-between mb-3">
            <p className="text-[11px] font-bold text-muted uppercase tracking-widest">Your Dispute</p>
            {dispute.status === 'Open' && <Badge label="Open" variant="danger" />}
            {dispute.status === 'UnderReview' && <Badge label="Under Review" variant="warning" />}
            {dispute.status === 'Resolved' && <Badge label="Resolved" variant="success" />}
            {dispute.status === 'Closed' && <Badge label="Closed" variant="muted" />}
          </div>
          <p className="text-sm text-text-secondary">{dispute.description}</p>
          {(dispute.status === 'Resolved' || dispute.status === 'Closed') && dispute.resolution && (
            <div className="mt-3 pt-3 border-t border-line">
              <p className="text-[11px] font-bold text-muted uppercase tracking-widest mb-1">Resolution</p>
              <p className="text-sm text-text">{dispute.resolution}</p>
            </div>
          )}
        </div>
        </AnimateIn>
      )}

      {/* Actions */}
      {(['Pending', 'Quoted', 'Accepted', 'Cancelled'].includes(request.status) ||
        DISPUTABLE_STATUSES.includes(request.status)) && (
        <div className="flex flex-col gap-2 mt-1">
          {request.status === 'Quoted' && request.quote && !isQuoteExpired && request.quote.counterOfferStatus !== 'PENDING' && (
            <Button
              onClick={() => setAcceptOpen(true)}
              className="!text-sm normal-case !tracking-normal rounded-2xl !px-4 !py-3"
            >
              Accept Quote
            </Button>
          )}
          {request.status === 'Quoted' && request.quote && !isQuoteExpired && (request.quote.counterOfferStatus ?? 'NONE') === 'NONE' && (
            <Button
              variant="secondary"
              onClick={() => setCounterOpen(true)}
              className="!text-sm normal-case !tracking-normal rounded-2xl !px-4 !py-3"
            >
              Propose a counter-offer
            </Button>
          )}
          {['Pending', 'Quoted', 'Accepted'].includes(request.status) && (
            <Button
              variant="danger"
              onClick={() => setCancelOpen(true)}
              className="!text-sm normal-case !tracking-normal !border-0 rounded-2xl !px-4 !py-3"
            >
              Cancel Booking
            </Button>
          )}
          {DISPUTABLE_STATUSES.includes(request.status) && !dispute && !isDisputeWindowExpired && (
            <Button
              variant="ghost"
              onClick={() => setDisputeOpen(true)}
              className="!text-sm !text-warning normal-case !tracking-normal !bg-warning/10 rounded-2xl !px-4 !py-3 hover:!bg-warning hover:!text-black"
            >
              <AlertTriangle size={15} />
              Raise a Dispute
            </Button>
          )}
          {request.status === 'Cancelled' && (
            <Button
              variant="danger"
              onClick={() => setDeleteOpen(true)}
              className="!text-sm normal-case !tracking-normal !border-0 rounded-2xl !px-4 !py-3"
            >
              Delete Request
            </Button>
          )}
        </div>
      )}

      {/* Accept quote confirmation */}
      {request.quote && (
        <ConfirmModal
          open={acceptOpen}
          onClose={() => setAcceptOpen(false)}
          onConfirm={handleAcceptQuote}
          title="Accept this quote?"
          message={`You're accepting a total of ${fmt(request.quote.total)}. Your job will be confirmed and a driver will be assigned soon.`}
          confirmLabel="Accept Quote"
          loading={acceptQuote.isPending}
        />
      )}

      {/* Cancel booking confirmation */}
      <ConfirmModal
        open={cancelOpen}
        onClose={() => setCancelOpen(false)}
        onConfirm={handleCancel}
        title="Cancel this booking?"
        message="This action cannot be undone. Your request will be cancelled and you will need to submit a new one if you change your mind."
        confirmLabel="Cancel Booking"
        cancelLabel="Keep Booking"
        danger
        loading={cancelRequest.isPending}
      />

      {/* Delete request confirmation */}
      <ConfirmModal
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        onConfirm={handleDelete}
        title="Delete this request?"
        message="This cannot be undone: the request and any associated quote or dispute will be permanently removed."
        confirmLabel="Delete Permanently"
        danger
        loading={deleteRequest.isPending}
      />

      {/* Counter-offer modal */}
      {request.quote && (
        <Modal open={counterOpen} onClose={() => setCounterOpen(false)} title="Propose a counter-offer" hideCloseButton>
          <div className="flex flex-col gap-4">
            <p className="text-sm text-text-secondary">
              Propose a new total below {fmt(request.quote.total)}. You get one counter-offer per quote.
            </p>
            <div>
              <label className="block text-[11px] font-bold text-muted uppercase tracking-widest mb-2">
                Proposed total (must be less than {fmt(request.quote.total)})
              </label>
              <input
                type="number"
                min={0}
                max={request.quote.total - 1}
                value={counterAmount}
                onChange={(e) => setCounterAmount(e.target.value)}
                placeholder="0"
                className="w-full rounded-xl border border-line bg-surface-raised px-3 py-2.5 text-sm text-text placeholder-muted outline-none focus:border-accent transition-colors"
              />
            </div>
            <div>
              <label className="block text-[11px] font-bold text-muted uppercase tracking-widest mb-2">
                Note (optional)
              </label>
              <textarea
                rows={3}
                value={counterNote}
                onChange={(e) => setCounterNote(e.target.value)}
                placeholder="Let us know why you're proposing this price..."
                className="w-full rounded-xl border border-line bg-surface-raised px-3 py-2.5 text-sm text-text placeholder-muted outline-none focus:border-accent transition-colors resize-none"
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="secondary" onClick={() => setCounterOpen(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleCounterSubmit}
                loading={submitCounterOffer.isPending}
                disabled={!counterAmount || Number(counterAmount) <= 0 || Number(counterAmount) >= request.quote.total}
              >
                Send Counter-Offer
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Dispute modal */}
      <Modal open={disputeOpen} onClose={() => setDisputeOpen(false)} title="Raise a dispute" hideCloseButton>
        <div className="flex flex-col gap-4">
          <p className="text-sm text-text-secondary">
            Describe the issue with your booking. Our team will review it within 24 hours.
          </p>
          <div>
            <label className="block text-[11px] font-bold text-muted uppercase tracking-widest mb-2">
              Description
            </label>
            <textarea
              rows={5}
              value={disputeText}
              onChange={(e) => setDisputeText(e.target.value)}
              placeholder="What went wrong? Include any relevant details..."
              className="w-full rounded-xl border border-line bg-surface-raised px-3 py-2.5 text-sm text-text placeholder-muted outline-none focus:border-accent transition-colors resize-none"
            />
          </div>
          <div>
            <label className="block text-[11px] font-bold text-muted uppercase tracking-widest mb-2">
              Photos <span className="normal-case font-medium text-muted/70">(optional, up to {MAX_EVIDENCE_PHOTOS})</span>
            </label>
            <div className="flex gap-2 flex-wrap">
              {disputeEvidence.map((url, i) => (
                <div key={url} className="relative w-20 h-20">
                  <img src={url} alt="Evidence" className="w-full h-full object-cover rounded-xl" />
                  <button
                    type="button"
                    onClick={() => setDisputeEvidence((prev) => prev.filter((_, idx) => idx !== i))}
                    className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-surface border border-line flex items-center justify-center text-muted hover:text-text transition-colors"
                    aria-label="Remove photo"
                  >
                    <X size={12} />
                  </button>
                </div>
              ))}
              {disputeEvidence.length < MAX_EVIDENCE_PHOTOS && (
                <button
                  type="button"
                  onClick={() => evidenceInputRef.current?.click()}
                  disabled={uploadingEvidence}
                  className="flex flex-col items-center justify-center gap-1 w-20 h-20 rounded-xl border border-line bg-surface-raised hover:bg-surface transition-colors disabled:opacity-60"
                >
                  {uploadingEvidence ? (
                    <Loader2 size={16} className="text-muted animate-spin" />
                  ) : (
                    <ImagePlus size={16} className="text-muted" />
                  )}
                  <span className="text-[10px] text-muted">{uploadingEvidence ? 'Uploading…' : 'Add photo'}</span>
                </button>
              )}
            </div>
            <input
              ref={evidenceInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className="hidden"
              onChange={(e) => handleEvidenceFile(e.target.files?.[0])}
            />
            {evidenceError && <p className="text-xs text-danger mt-2">{evidenceError}</p>}
          </div>
          <div className="flex gap-2 justify-end">
            <Button
              variant="secondary"
              onClick={() => {
                setDisputeOpen(false);
                setDisputeText('');
                setDisputeEvidence([]);
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleDisputeSubmit}
              loading={createDispute.isPending}
              disabled={!disputeText.trim() || uploadingEvidence}
            >
              Submit Dispute
            </Button>
          </div>
        </div>
      </Modal>

      {/* Payment details modal — banking info for a manual transfer; there's no online payment
          gateway in this app, "paid" is a status admin sets by hand once a transfer's received. */}
      {invoice && (
        <Modal open={paymentModalOpen} onClose={() => setPaymentModalOpen(false)} title="Payment Details">
          <div className="flex flex-col gap-4">
            <div className="rounded-3xl border border-line bg-surface-raised p-4">
              <p className="text-[11px] font-bold text-muted uppercase tracking-widest mb-1">Amount due</p>
              <div className="flex items-end justify-between gap-4">
                <div>
                  <p className="text-xs text-text-secondary">Manual bank transfer only</p>
                  <p className="text-xl font-black text-accent mt-1">{fmt(invoice.total)}</p>
                </div>
                <Badge label={invoice.status === 'paid' ? 'Paid' : 'Unpaid'} variant={invoice.status === 'paid' ? 'success' : 'warning'} />
              </div>
            </div>

            {bankInfoLoading ? (
              <div className="flex justify-center py-6">
                <Spinner size="md" />
              </div>
            ) : bankInfo && bankInfo.length > 0 ? (
              <div className="flex flex-col gap-4">
                {bankInfo.map((account, i) => (
                  <div key={account.id} className="flex flex-col gap-3 rounded-2xl border border-line bg-bg p-4">
                    {(bankInfo.length > 1 || account.label) && (
                      <span className="text-xs font-bold uppercase tracking-wide text-muted">
                        {account.label ?? `Account ${i + 1}`}
                      </span>
                    )}
                    {[
                      { label: 'Bank', value: account.bankName },
                      { label: 'Account name', value: account.bankAccountName },
                      { label: 'Account number', value: account.bankAccountNumber },
                      { label: 'Account type', value: account.bankAccountType === 'savings' ? 'Savings' : account.bankAccountType === 'checking' ? 'Checking' : null },
                      { label: 'Branch', value: account.bankBranch },
                      { label: 'SWIFT code', value: account.bankSwiftCode },
                    ].filter((row) => row.value).map((row) => (
                      <div key={row.label} className="flex items-center justify-between text-sm">
                        <span className="text-muted">{row.label}</span>
                        <span className="font-semibold text-text">{row.value}</span>
                      </div>
                    ))}
                  </div>
                ))}
                <div className="rounded-2xl border border-accent/20 bg-accent/10 px-4 py-3">
                  <p className="text-xs font-bold uppercase tracking-widest text-accent">Payment reference</p>
                  <p className="text-xs text-text-secondary mt-1">
                    Please include <span className="font-mono font-bold text-text">#{request.id.slice(-6).toUpperCase()}</span> as your payment reference so we can match it to this booking.
                  </p>
                </div>
              </div>
            ) : (
              <p className="text-sm text-muted">
                Banking details aren't set up yet, please contact us directly to arrange payment.
              </p>
            )}

            <Button variant="secondary" onClick={() => setPaymentModalOpen(false)} className="rounded-2xl !py-3.5">
              Close
            </Button>
          </div>
        </Modal>
      )}
    </div>
  );
}
